import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, BrainCircuit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader, StatusBadge } from "@/components/app/ui";
import { PolicyDialog } from "@/components/protection/PolicyDialog";
import { RedactionTool } from "@/components/protection/RedactionTool";
import { api, errMsg, can } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const scopeText = (s) => ["roles", "environments", "purposes", "source_ids"].map((k) => (s?.[k]?.length ? `${k.replace("_ids", "s")}: ${s[k].join(", ")}` : null)).filter(Boolean).join(" · ") || "Applies everywhere";

export default function DataProtection() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [pols, setPols] = useState([]);
  const [sources, setSources] = useState([]);
  const [ai, setAi] = useState(true);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/protection/policies").then((r) => setPols(r.data));
  useEffect(() => {
    api.get("/protection/meta").then((r) => setMeta(r.data)); load();
    api.get("/data-sources").then((r) => setSources(r.data)); api.get("/protection/settings").then((r) => setAi(r.data.ai_pii_protection));
  }, []);
  const w = can(user, "datasource");
  const label = (k) => meta?.methods.find((m) => m.key === k)?.label || k;
  const setAiOn = async (v) => { try { await api.put("/protection/settings", { ai_pii_protection: v }); setAi(v); toast.success(v ? "PII is now tokenised before AI processing" : "Protect-before-AI turned off"); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (p) => { if (!window.confirm(`Delete policy "${p.name}"?`)) return; await api.delete(`/protection/policies/${p.id}`); load(); };
  if (!meta) return null;
  return (
    <div>
      <PageHeader title="Data Protection" testId="page-title" subtitle="Discovery → Classification → Policy → Mask / Redact → Audit. Configure how personal data is shown, exported and sent to AI." />
      <div className="mb-5 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50/50 p-4" data-testid="ai-protection-card">
        <BrainCircuit className="mt-0.5 h-5 w-5 text-blue-700" />
        <div className="flex-1"><div className="text-sm font-semibold text-slate-800">Protect Before AI Processing</div>
          <p className="text-xs text-slate-600">Detected PII (Aadhaar, PAN, UPI, email, phone, card, IFSC, IP) in documents and Copilot context is replaced with consistent tokens before anything is sent to an AI model.</p></div>
        <Switch checked={ai} disabled={!w} onCheckedChange={setAiOn} data-testid="ai-protection-switch" />
      </div>
      <Tabs defaultValue="policies">
        <TabsList><TabsTrigger value="policies" data-testid="tab-policies">Protection policies</TabsTrigger><TabsTrigger value="tool" data-testid="tab-tool">Mask & redact tool</TabsTrigger></TabsList>
        <TabsContent value="policies" className="space-y-3 pt-3">
          {w && <Button onClick={() => setEdit({})} className="bg-blue-600 hover:bg-blue-700" data-testid="new-policy-button"><Plus className="mr-1.5 h-4 w-4" />New policy</Button>}
          {pols.map((p) => (
            <div key={p.id} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`policy-${p.id}`}>
              <div className="flex flex-wrap items-center gap-2"><span className="font-semibold text-slate-800">{p.name}</span><StatusBadge status={p.active ? "Active" : "Inactive"} />
                <span className="text-xs text-slate-500">Priority {p.priority} · {scopeText(p.scope)}</span>
                {w && <span className="ml-auto flex"><Button size="icon" variant="ghost" onClick={() => setEdit(p)} data-testid={`policy-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(p)} data-testid={`policy-delete-${p.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button></span>}</div>
              <div className="mt-2 flex flex-wrap gap-1.5">{Object.entries(p.rules).map(([k, v]) => <span key={k} className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600"><b>{meta.categories.find((c) => c.key === k)?.label || k}</b> → {label(v)}</span>)}</div>
            </div>))}
        </TabsContent>
        <TabsContent value="tool" className="pt-3"><RedactionTool meta={meta} /></TabsContent>
      </Tabs>
      <PolicyDialog policy={edit} meta={meta} sources={sources} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
